import {
    TextInput,
    PhoneInput,
    SelectInput,
} from '~/shared/components/form/Inputs';
import { CountryStateFields } from '~/shared/components/form/CountryStateFields';
import states from '~/shared/states.json';
import PaymentOptions from './PaymentOptions';
import { useFormContext } from 'react-hook-form';
import { Card, FormGroup, Button } from 'reactstrap';
import { CheckBox } from '~/shared/components/form/CheckBox';
import { useContext, useEffect, useState } from 'react';
import { CampaignContext } from '~/context/campaign';
import { getDashColor } from '~/shared/components/icons';
import { renderIcon } from '~/shared/components/icons';
import { createVendor } from '~/services/fundrequests';
import { FundRequestContext } from '~/context/fundrequests';
import toast from 'react-hot-toast';
import { AdminContext } from '~/context/admin';
import { State } from 'country-state-city';

interface NewPayeeProps {
    payeesFound: boolean;
    onCancel?: () => void;
    onSave?: () => void;
}

const NewPayee = ({ payeesFound, onCancel, onSave }: NewPayeeProps): JSX.Element => {
    const [showAddAddress, setShowAddAddress] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [userEditedLabel, setUserEditedLabel] = useState(false);
    const { control, watch, setValue, getValues, reset } = useFormContext();
    const checked = watch('clientPayee');
    const [{ campaignId }, adminDispatch] = useContext(AdminContext);
    const [{ campaign }] = useContext(CampaignContext);
    const [, fundRequestDispatch] = useContext(FundRequestContext);

    // Watch address_1 to set default address_label
    const address1 = watch('new_vendor.address_1');
    const addressLabel = watch('new_vendor.address_label');

    // Default country to US when the form first opens
    useEffect(() => {
        if (!getValues('new_vendor.country')) {
            setValue('new_vendor.country', 'US');
        }
    }, []);

    // Update address_label when address_1 changes and the user hasn't manually edited the label
    useEffect(() => {
        if (address1 && !userEditedLabel) {
            setValue('new_vendor.address_label', address1);
        }
    }, [address1, setValue, userEditedLabel]);

    // Handle manual changes to address_label
    const handleAddressLabelChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        // If the user types something different from address1, mark as user-edited
        if (e.target.value !== address1) {
            setUserEditedLabel(true);
        }

        // If they delete it completely, allow syncing with address1 again
        if (e.target.value === '') {
            setUserEditedLabel(false);
        }
    };

    useEffect(() => {
        const previousValues = getValues();
        if (checked) {
            const person = campaign?.patient?.person;

            if (person) {
                const newState = Object.keys(states).find(
                    (key) => states[key as keyof typeof states] === person.state
                );

                reset({
                    ...previousValues,
                    new_vendor: {
                        name: person.first_name + ' ' + person.last_name,
                        address_1: person.address_1,
                        address_2: person.address_2,
                        city: person.city,
                        state: newState,
                        country: 'US',
                        zip_code: person.zip_code,
                        phone: person.phone,
                        email: person.email,
                    },
                });

                // Reset the user edited flag when populating the client address
                setUserEditedLabel(false);
            } else {
                reset({
                    ...previousValues,
                    new_vendor: null,
                });
            }
        } else {
            reset({
                ...previousValues,
                new_vendor: null,
            });

            // Reset the user edited flag when clearing the form
            setUserEditedLabel(false);
        }
    }, [checked]);

    const handleCancel = () => {
        if (onCancel) {
            onCancel();
        }

        setValue('clientPayee', false);
        setShowAddAddress(false);
        setUserEditedLabel(false);

        setTimeout(() => {
            reset({
                new_vendor: null,
                clientPayee: false,
            });
        }, 0);
    };

    const handleSaveVendor = async () => {
        try {
            setIsSubmitting(true);
            const formValues = getValues();
            const newVendorData = formValues.new_vendor;

            if (!newVendorData || !newVendorData.name) {
                toast.error('Payee name is required');
                setIsSubmitting(false);
                return;
            }

            const selectedCountry = newVendorData.country || 'US';
            const isUS = selectedCountry === 'US';
            const hasSubdivisions =
                !isUS && State.getStatesOfCountry(selectedCountry).length > 0;

            const missingFields: string[] = [];
            if (!newVendorData.address_1) missingFields.push('Street Address 1');
            if (!newVendorData.city) missingFields.push('City');
            if (isUS && !newVendorData.state) missingFields.push('State');
            if (hasSubdivisions && !newVendorData.state_province) {
                missingFields.push('State/Province');
            }
            if (!newVendorData.zip_code) {
                missingFields.push(isUS ? 'Zip Code' : 'Postal Code');
            }

            if (missingFields.length > 0) {
                toast.error(`Required fields missing: ${missingFields.join(', ')}`);
                setIsSubmitting(false);
                return;
            }

            // Ensure payment type is selected
            const payment_type = formValues.payment_type;
            if (!payment_type) {
                toast.error('Payment method is required');
                setIsSubmitting(false);
                return;
            }

            // Make sure we have an address label
            if (!newVendorData.address_label) {
                newVendorData.address_label = 'Primary Address';
            }

            // Separate vendor fields from address fields
            const vendorData = {
                name: newVendorData.name,
                phone: newVendorData.phone,
                email: newVendorData.email,
                payment_type
            };

            // Address fields will be handled by the backend
            // We need to pass them at the top level
            const addressData = {
                address_1: newVendorData.address_1,
                address_2: newVendorData.address_2,
                city: newVendorData.city,
                country: selectedCountry,
                state: isUS ? newVendorData.state : undefined,
                state_province: !isUS ? newVendorData.state_province : undefined,
                zip_code: newVendorData.zip_code,
                address_label: newVendorData.address_label
            };

            // Create the new vendor with both vendor and address data
            const response = await createVendor(
                { ...vendorData, ...addressData },
                campaignId
            );

            // Check if the response contains an error
            if ('error' in response) {
                toast.error(`Failed to save payee: ${response.error}`);
                return;
            }

            // Add the new vendor to the vendors list in context
            fundRequestDispatch({
                type: 'add_vendor',
                payload: response
            });

            toast.success('Payee saved successfully');

            // Reset the form
            setValue('clientPayee', false);
            setValue('new_vendor', null);

            // Call the onSave callback if provided
            if (onSave) {
                onSave();
            }
        } catch (error) {
            console.error('Error saving vendor:', error);
            toast.error('Failed to save payee. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div
            className={` ${payeesFound && 'border border-3 border-primary rounded p-3'
                }`}
        >
            {payeesFound && <h6 style={{ fontSize: 18 }}>New Payee</h6>}
            <Card
                className='mb-3 fw-bold'
                style={{
                    padding: '12px 20px',
                    background: getDashColor('supporters'),
                    fontSize: '18px',
                }}
            >
                All new payees need to be verified by Help Hope Live before we
                can process any payments.
            </Card>

            <FormGroup check className='mb-3 ps-0'>
                <CheckBox
                    name='clientPayee'
                    label='The client is the payee'
                    value={checked}
                    onChange={() => {
                        setValue('clientPayee', !checked);
                    }}
                />
            </FormGroup>

            <h6 style={{ fontSize: '18px' }}>Contact</h6>
            <TextInput
                label='Name'
                name='new_vendor.name'
                control={control}
                required={!watch('vendor')}
                aria-required={!watch('vendor')}
            />
            <PhoneInput
                label='Phone'
                control={control}
                name='new_vendor.phone'
                required={false}
            />
            <TextInput
                label='Email'
                control={control}
                name='new_vendor.email'
                type='email'
                required={false}
            />

            <h6 style={{ fontSize: '18px' }}>Address</h6>
            <p>This will serve as the primary address for this payee</p>

            <TextInput
                label='Street Address 1'
                control={control}
                name='new_vendor.address_1'
                required={!watch('vendor')}
                aria-required={!watch('vendor')}
            />
            <TextInput
                label='Street Address 2'
                control={control}
                name='new_vendor.address_2'
            />

            <CountryStateFields
                control={control}
                watch={watch}
                setValue={setValue}
                countryFieldName='new_vendor.country'
                stateFieldName='new_vendor.state'
                stateProvinceFieldName='new_vendor.state_province'
                cityFieldName='new_vendor.city'
                zipFieldName='new_vendor.zip_code'
                required={!watch('vendor')}
            />

            <TextInput
                label='Address Label'
                name='new_vendor.address_label'
                type='text'
                helperText="for example: Main House, Grandma's House, etc."
                control={control}
                required={!watch('vendor')}
                aria-required={!watch('vendor')}
                onChange={handleAddressLabelChange}
            />

            {showAddAddress && (
                <>
                    <hr />
                    <TextInput
                        label='Street Address 1'
                        control={control}
                        name='new_vendor.address_1'
                        required={!watch('vendor')}
                        aria-required={!watch('vendor')}
                    />
                    <TextInput
                        label='Street Address 2'
                        control={control}
                        name='new_vendor.address_2'
                    />
                    <TextInput
                        label='City'
                        name='new_vendor.city'
                        control={control}
                        required={!watch('vendor')}
                        aria-required={!watch('vendor')}
                    />

                    <div style={{ display: 'flex', width: '100%' }}>
                        <span style={{ width: '75%', marginRight: '10px' }}>
                            <SelectInput
                                label='State'
                                control={control}
                                name='new_vendor.state'
                                options={Object.entries(states).map(
                                    ([accronym, label]) => ({
                                        value: accronym,
                                        label: label,
                                    })
                                )}
                                required={!watch('vendor')}
                                aria-required={!watch('vendor')}
                            />
                        </span>
                        <span style={{ width: '25%' }}>
                            <TextInput
                                label='Zip Code'
                                control={control}
                                name='new_vendor.zip_code'
                                required={!watch('vendor')}
                                aria-required={!watch('vendor')}
                            />
                        </span>
                        <TextInput
                            label='Address Label'
                            name='new_vendor.address_label'
                            type='text'
                            helperText="for example: Main House, Grandma's House, etc."
                            control={control}
                        />
                    </div>
                </>
            )}

            <PaymentOptions />

            <div className='d-flex mt-4'>
                <Button color='outline-dark' size='sm' onClick={handleCancel}>
                    Cancel
                </Button>
                <Button
                    color='primary'
                    size='sm'
                    className='ms-2'
                    onClick={handleSaveVendor}
                    disabled={isSubmitting}
                >
                    {isSubmitting ? 'Saving...' : 'Save Payee'}
                </Button>
            </div>
        </div>
    );
};

export default NewPayee; 